import { fetchBaseQuery, BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/dist/query/react';
import * as Sentry from '@sentry/react';
import { BASE_URL } from 'shared/conf/constants';
import { RootState } from 'app/store';
import { setLogIn, setLogOut } from 'entities/auth';

export const baseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  headers: {},
});

export const baseQueryWithJWT = fetchBaseQuery({
  baseUrl: BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    const { token } = (getState() as RootState).auth;

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  },
});

const makeRefreshRequest = async (api: any, extraOptions: any) => {
  const refreshResult = await baseQuery(
    {
      url: '/account/admin/login/refresh-token',
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('refresh_token')}`,
      },
    },
    api,
    extraOptions,
  );

  return refreshResult;
};

const handleRefreshError = (api: any) => {
  api.dispatch(setLogOut());
};

const handleRefreshSuccess = (api: any, data: { access_token: string; refresh_token: string }) => {
  const { access_token, refresh_token } = data;
  api.dispatch(setLogIn({ access_token, refresh_token }));
};

const handleRefreshToken = async (api: any, extraOptions: any) => {
  try {
    const refreshResult = await makeRefreshRequest(api, extraOptions);

    if (refreshResult.error && refreshResult.error.status === 401) {
      handleRefreshError(api);
      return false;
    }

    if (refreshResult.data) {
      handleRefreshSuccess(api, (refreshResult as any).data);
      return true;
    }

    return false;
  } catch (error) {
    return false;
  }
};

export const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  let result = await baseQueryWithJWT(args, api, extraOptions);

  if (result.error) {
    Sentry.captureException(result.error);
  }

  if (result.error && result.error.status === 401) {
    if (localStorage.getItem('refresh_token') === null) {
      api.dispatch(setLogOut());
    } else {
      const refreshTokenSuccess = await handleRefreshToken(api, extraOptions);
      if (refreshTokenSuccess) {
        result = await baseQueryWithJWT(args, api, extraOptions);
      }
    }
  }

  return result;
};
